import React from "react";
import { motion } from "framer-motion";
import { Check, Info } from "lucide-react";

const frontOfficeAgents = [
  "Amy — Receptionist",
  "Leena — Schedule Optimizer",
  "Stephanie — Hygiene Recall",
  "Kim — Case Acceptance",
  "Elise — Patient Experience",
  "Morgan — Patient Intake",
  "Trisha — Practice Manager",
  "Joseph — Fax Manager",
];

const communicationSuite = [
  "VoIP Phone System",
  "2-Way Messaging",
  "Intelligent Fax",
  "Next-Gen Forms",
  "Team Chat",
  "Online Scheduling",
  "Physical Phones",
];

const marketingSuite = [
  "SMS Campaigns",
  "Email Campaigns",
  "Review Generation",
  "Patient Recalls",
  "Website & SEO",
  "Marketing Analytics",
];

const backOfficeAgents = [
  "Amanda — Insurance Verification",
  "Ariel — Claim Submission",
  "Alayna — Claim Scrubbing",
  "Sarah — Denial Management",
  "Alisha — EOB Posting",
  "Kianna — Patient Billing",
  "Julia — AR & Collections",
  "Laura — Insurance Calls",
];

const revenueIntelligenceSuite = [
  "Revenue Analytics Dashboard",
  "Daily Huddles Dashboard",
  "Collections Reporting",
  "Production Tracking",
  "Provider Performance",
  "AR Aging Reports",
];

const integratedPaymentsSuite = [
  "Card Readers",
  "Text-to-Pay",
  "Payment Processing",
  "Patient Payment Plans",
];

const claimsPortal = [
  "Unified Claim Submission",
  "Real-time Claim Status",
  "Batch Claim Processing",
];

const insuranceVerification = [
  "Real-time Benefits Check",
  "Comprehensive Breakdowns",
  "Eligibility Monitoring",
];

const FeatureGroup = ({ title, items, accent }) => (
  <div>
    <p className="text-[12px] font-bold text-slate-500 uppercase tracking-wider mb-2">
      {title}
    </p>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <Check className={`w-3.5 h-3.5 ${accent} shrink-0 mt-[3px]`} />
          <span className="text-[13px] text-slate-700 leading-snug">{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const IntegratePMSPricingSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1180px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5"
        >
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            Your PMS.{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Now with an AI workforce.
            </span>
          </h2>
        </motion.div>

        {/* Two-section pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full rounded-[20px] overflow-hidden shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] border border-white/80 bg-white/95"
        >
          {/* Accent line */}
          <div
            className="h-[3px]"
            style={{
              background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #3b82f6 100%)",
            }}
          />

          <div className="grid grid-cols-2 relative">
            {/* Bundled pricing indicator — sits over the divider at price row */}
            <div
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-[68px] z-10 w-10 h-10 rounded-full bg-white border border-blue-200 shadow-[0_4px_14px_-3px_rgba(59,130,246,0.3)] flex items-center justify-center"
            >
              <span className="text-xl font-bold text-blue-600 leading-none">+</span>
            </div>

            {/* LEFT — Front Office AI / $800 */}
            <div className="p-6 border-r border-slate-100 flex flex-col">
              <div className="mb-4 text-center">
                <p className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-1.5">
                  Front Office AI
                </p>
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-[44px] font-extrabold tracking-tight text-slate-900 leading-none">
                    $800
                  </span>
                  <span className="text-sm font-medium text-slate-400">/mo</span>
                </div>
              </div>

              <div className="space-y-3.5">
                <FeatureGroup
                  title="8 Front Office AI Agents"
                  items={frontOfficeAgents}
                  accent="text-blue-500"
                />
                <FeatureGroup
                  title="Communication Suite"
                  items={communicationSuite}
                  accent="text-sky-500"
                />
                <FeatureGroup
                  title="Marketing Suite"
                  items={marketingSuite}
                  accent="text-indigo-500"
                />
              </div>
            </div>

            {/* RIGHT — Back Office AI / 3.5% */}
            <div className="p-6 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30 flex flex-col">
              <div className="mb-4 text-center">
                <p className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-1.5">
                  Back Office AI
                </p>
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-[44px] font-extrabold tracking-tight text-slate-900 leading-none">
                    3.5%
                  </span>
                  <span className="text-sm font-medium text-slate-400">of collections</span>
                </div>
              </div>

              <div className="space-y-3.5">
                <FeatureGroup
                  title="8 Back Office AI Agents"
                  items={backOfficeAgents}
                  accent="text-indigo-500"
                />
                <FeatureGroup
                  title="Revenue Intelligence Suite"
                  items={revenueIntelligenceSuite}
                  accent="text-violet-500"
                />
                <div className="grid grid-cols-2 gap-3">
                  <FeatureGroup
                    title="Integrated Payments"
                    items={integratedPaymentsSuite}
                    accent="text-purple-500"
                  />
                  <FeatureGroup
                    title="Claims Portal"
                    items={claimsPortal}
                    accent="text-fuchsia-500"
                  />
                </div>
                <FeatureGroup
                  title="Insurance Verification Software"
                  items={insuranceVerification}
                  accent="text-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-100/80 px-7 py-3 flex items-center justify-between gap-4 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
              <span className="text-[13.5px] font-medium text-slate-600">
                <strong className="text-slate-700">150,000 AI credits</strong> included &middot;
                overage at <strong className="text-slate-700">2.3¢</strong> per 1,000 AI credits
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13.5px] font-medium text-slate-600">
              <Info className="w-3.5 h-3.5 text-slate-400" />
              <span>
                + <strong className="text-slate-700">$400/mo</strong> PMS integration fee &middot;
                Available for Dentrix, Eaglesoft, Open Dental
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IntegratePMSPricingSlide;
